/**
 * Global frontend error reporter.
 * Captures window.onerror + unhandledrejection and forwards them to the backend
 * Technical Error Log via POST /api/errors/report.
 *
 * Safe-by-design:
 *   - Throttles to max 10 reports / minute to avoid flooding on render loops
 *   - Never throws (swallows fetch errors silently)
 *   - Skips if backend URL missing
 *   - Ignores noise: ResizeObserver loop warnings, ChunkLoadError retries, aborted fetches
 */

const ENV_BACKEND = process.env.REACT_APP_BACKEND_URL;
// Mirror App.js: prefer same-origin to avoid cross-origin issues on apex/www.
const BACKEND_URL = (typeof window !== 'undefined' && window.location && window.location.origin)
  ? window.location.origin
  : ENV_BACKEND;
const REPORT_ENDPOINT = BACKEND_URL ? `${BACKEND_URL}/api/errors/report` : null;
const MAX_PER_WINDOW = 10;
const WINDOW_MS = 60 * 1000;

let recentReports = [];
let installed = false;

const IGNORE_PATTERNS = [
  /ResizeObserver loop/i,
  /Non-Error promise rejection captured/i,
  /Script error\.?$/,
  /ChunkLoadError/i,
  /Loading chunk \d+ failed/i,
  /AbortError/i,
];

function shouldIgnore(message) {
  if (!message) return true;
  return IGNORE_PATTERNS.some((re) => re.test(message));
}

function withinRateLimit() {
  const now = Date.now();
  recentReports = recentReports.filter((t) => now - t < WINDOW_MS);
  if (recentReports.length >= MAX_PER_WINDOW) return false;
  recentReports.push(now);
  return true;
}

async function sendReport(payload) {
  if (!REPORT_ENDPOINT) return;
  if (shouldIgnore(payload.message)) return;
  if (!withinRateLimit()) return;
  try {
    await fetch(REPORT_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    // Swallow — never throw from the error reporter
  }
}

export function installErrorReporter() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (event) => {
    const message = event?.message || event?.error?.message || 'Unknown error';
    const stack = event?.error?.stack || `${event?.filename || ''}:${event?.lineno || 0}:${event?.colno || 0}`;
    sendReport({
      message: String(message).slice(0, 500),
      url: window.location.pathname + window.location.search,
      stack: String(stack).slice(0, 2000),
      userId: localStorage.getItem('userId') || sessionStorage.getItem('userId') || '',
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event?.reason;
    const message = (reason && (reason.message || String(reason))) || 'Unhandled promise rejection';
    const stack = (reason && reason.stack) || '';
    sendReport({
      message: `[unhandledrejection] ${String(message).slice(0, 460)}`,
      url: window.location.pathname + window.location.search,
      stack: String(stack).slice(0, 2000),
      userId: localStorage.getItem('userId') || sessionStorage.getItem('userId') || '',
    });
  });
}

// Exposed for manual smoke test from devtools: window.__reportTestError()
if (typeof window !== 'undefined') {
  window.__reportTestError = (msg = 'Manual test error from console') => {
    sendReport({
      message: msg,
      url: window.location.pathname,
      stack: 'Manual trigger via window.__reportTestError',
      userId: '',
    });
    return 'Test error reported';
  };
}
